import {ApolloClient, ApolloLink, InMemoryCache, from, split} from "@apollo/client";
import {setContext} from "@apollo/client/link/context";
import type {TStore} from "./types";
import {createErrorHandlerLink} from "./errors";
import {Observable, getMainDefinition} from "@apollo/client/utilities";
import {WebSocketLink} from "./webSocketLink";
import {v4 as uuid} from "uuid";
import {startGraphqlQuery, stopGraphqlQuery} from "@/actions";
// @ts-expect-error - apollo-upload-client has export issues with its types
import {createUploadLink} from "apollo-upload-client";
import {omitTypenames} from "@/tools";
import {UserManager} from "oidc-client-ts";

const cache = new InMemoryCache();

const cleanTypeName = new ApolloLink((operation, forward) => {
    if (operation.variables) {
        operation.variables = omitTypenames(operation.variables);
    }
    return forward(operation).map((data) => {
        return data;
    });
});

export const rollbackOnErrorVarName = "rollbackOnError";

export const createRollbackOnErrorContext = () => ({[rollbackOnErrorVarName]: true});

const addRollBackOnFailExtension = new ApolloLink((operation, forward) => {
    if (operation.getContext()[rollbackOnErrorVarName]) operation.extensions[rollbackOnErrorVarName] = true;
    return forward(operation);
});

export type ResetCacheAfterEveryMutationOptions = {
    reset: boolean;
};

export const resetCacheAfterEveryMutationOptions: ResetCacheAfterEveryMutationOptions = {
    reset: true,
};

/**
 * Whitelist of query field names that should not be refetched after every mutation
 *
 * To always show the latest state of everything on the website, we used to invalidate the whole cache after
 * every single mutation (including translations, structureConfig, etc.). This was a bit overkill and caused
 * a lot of unnecessary refetches.
 *
 * Although this is not the best solution, it is a good start by already reducing the amount of unnecessary GraphQL
 * traffix by a huge amount.
 * */
const MUTATION_CACHE_RESET_WHITELIST = [
    // this would just fetch if the Query type named 'Query'
    "__typename" as const,

    "structureConfig" as const,
    "translations" as const,
    "limitsSummary" as const,
] as const;

const resetCacheAfterEveryMutation = new ApolloLink((operation, forward) => {
    if (!resetCacheAfterEveryMutationOptions.reset) return forward(operation);

    // only reset cache after mutations
    if (operation.query.definitions.some((def) => def.kind === "OperationDefinition" && def.operation === "mutation")) {
        // using an observable to let apollo handle async operations more efficiently
        return new Observable((observer) => {
            const subscription = forward(operation).subscribe({
                next: observer.next.bind(observer), //default next and error handlers
                error: observer.error.bind(observer),
                complete: () => {
                    const cache = operation.getContext().cache as unknown as InMemoryCache;

                    // invalidate all cache data except the ones that are included in the whitelist
                    cache.modify({
                        broadcast: false,
                        fields: (existingFields, {DELETE, fieldName}) => {
                            if (MUTATION_CACHE_RESET_WHITELIST.includes(fieldName as any)) {
                                return existingFields;
                            }
                            return DELETE;
                        },
                    });

                    observer.complete();
                },
            });

            // if not yet unsubscribed when the observable is dropped, unsubscribe
            return () => {
                if (subscription) subscription.unsubscribe();
            };
        });
    }
    return forward(operation);
});

const createQueryRegister = (store: TStore) => {
    return new ApolloLink((operation, forward) => {
        return new Observable((observer) => {
            const id = uuid();
            operation.setContext({id});
            store.dispatch(startGraphqlQuery(id));

            const subscription = forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
            });

            return () => {
                if (subscription) {
                    try {
                        store.dispatch(stopGraphqlQuery(id));
                    } catch (e) {
                        console.error("Error dispatching stopGraphqlQuery", e);
                    }
                    subscription.unsubscribe();
                }
            };
        });
    });
};

export const createClient = (store: TStore, userManager: UserManager) => {
    const webSocketProtocol = window.location.protocol === "https:" ? "wss" : "ws";
    const wsLink = new WebSocketLink(
        {
            url: `${webSocketProtocol}://${window.location.host}/api/ws-graphql`,
        },
        userManager,
    );

    // Auth link - only adds token if userManager is provided and user is authenticated
    const authLink = setContext(async (_, {headers}) => {
        if (!userManager) {
            return {headers};
        }

        try {
            const user = await userManager.getUser();
            const token = user?.access_token;

            return {
                headers: {
                    ...headers,
                    ...(token && {authorization: `Bearer ${token}`}),
                },
            };
        } catch (error) {
            console.warn("Failed to get access token:", error);
            return {headers};
        }
    });

    return new ApolloClient({
        cache,
        link: from([
            addRollBackOnFailExtension,
            cleanTypeName,
            createQueryRegister(store),
            authLink,
            createErrorHandlerLink(store, userManager),
            resetCacheAfterEveryMutation,
            // TODO: if this is enabled, the websocket dies
            // apolloLogger,
            split(
                // split based on operation type
                ({query}) => {
                    const definition = getMainDefinition(query);
                    return definition.kind === "OperationDefinition" && definition.operation === "subscription";
                },
                wsLink,
                createUploadLink({uri: "/api/graphql", includeExtensions: true}),
            ),
        ]),
    });
};
